import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Input,
} from "reactstrap";
import { Down24, Remove24 } from "@bphxd/ds-core-react/lib/icons";
import "./TaxFilter.scss";
import { Button } from "@bphxd/ds-core-react";

interface TaxFilterProps {
  regionData: any;
  taxFilerDetails: any;
  handleFilterApply: (filters: any) => void;
}

interface FilterState {
  region: string[];
  country: string[];
  port: string[];
  bussiness_channel: string[];
  cust_sales_area: string[];
  sales_country: string[];
  order_number: string[];
  cust_code: string[];
  cust_name: string[];
  vessel_name: string[];
  vessel_imo_number: string[];
  account_manager: string[];
  tax_document_type: string[];
  status: string[];
}

interface SearchState {
  region: string;
  country: string;
  port: string;
  bussiness_channel: string;
  cust_sales_area: string;
  sales_country: string;
  order_number: string;
  cust_code: string;
  cust_name: string;
  vessel_name: string;
  vessel_imo_number: string;
  account_manager: string;
}

const TaxFilter: React.FC<TaxFilterProps> = ({
  regionData,
  taxFilerDetails,
  handleFilterApply,
}) => {
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    region: [],
    country: [],
    port: [],
    bussiness_channel: [],
    cust_sales_area: [],
    sales_country: [],
    order_number: [],
    cust_code: [],
    cust_name: [],
    vessel_name: [],
    vessel_imo_number: [],
    account_manager: [],
    tax_document_type: [],
    status: [],
  });

  const [searchText, setSearchText] = useState<SearchState>({
    region: "",
    country: "",
    port: "",
    bussiness_channel: "",
    cust_sales_area: "",
    sales_country: "",
    order_number: "",
    cust_code: "",
    cust_name: "",
    vessel_name: "",
    vessel_imo_number: "",
    account_manager: "",
  });

  const [countryOptions, setCountryOptions] = useState<any[]>([]);
  const [portOptions, setPortOptions] = useState<string[]>([]);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const prevFiltersRef = useRef<FilterState | null>(null);

  const taxDocumentTypes = ["Periodical", "Transactional", "Both"];
  const statusOptions = ["Valid", "Invalid", "Expired", "Pending for Validation"];
  const hasActiveFilters = useMemo(() => {
    return (Object.keys(selectedFilters) as Array<keyof FilterState>).some(
      (key) => selectedFilters[key].length > 0
    );
  }, [selectedFilters]);
  const filterOptions = useMemo(
    () => ({
      bussiness_channel: taxFilerDetails?.bussiness_channel || [],
      cust_sales_area: taxFilerDetails?.cust_sales_area || [],
      sales_country: taxFilerDetails?.sales_country || [],
      order_number: taxFilerDetails?.order_number || [],
      cust_code: taxFilerDetails?.cust_code || [],
      cust_name: taxFilerDetails?.cust_name || [],
      vessel_name: taxFilerDetails?.vessel_name || [],
      vessel_imo_number: taxFilerDetails?.vessel_imo_number || [],
      account_manager: taxFilerDetails?.account_manager || [],
    }),
    [taxFilerDetails]
  );

  const getCountriesForRegions = useCallback((regions: string[]) => {
    const countriesSet = new Set<any>();

    regions.forEach((region) => {
      const regionData_item = regionData.find(
        (rgn: any) => rgn.region_name === region
      );
      if (
        regionData_item?.country_name &&
        Array.isArray(regionData_item.country_name)
      ) {
        regionData_item.country_name.forEach((country: any) => {
          countriesSet.add(JSON.stringify(country));
        });
      }
    });

    return Array.from(countriesSet)
      .map((country) => JSON.parse(country as string))
      .sort((a: any, b: any) => {
        const aName = a?.name || a || "";
        const bName = b?.name || b || "";
        if (aName < bName) return -1;
        if (aName > bName) return 1;
        return 0;
      });
  }, [regionData]);

  const getPortsForCountries = useCallback(
    (countries: string[], countryData: any[]) => {
      const portsSet = new Set<string>();

      countries.forEach((country) => {
        const countryDataItem = countryData.find(
          (cty: any) => cty.name === country
        );
        if (
          countryDataItem?.port_name &&
          Array.isArray(countryDataItem.port_name)
        ) {
          countryDataItem.port_name.forEach((port: string) => {
            portsSet.add(port);
          });
        }
      });

      return Array.from(portsSet).sort();
    },
    []
  );

  const handleRegionSelect = useCallback((region: string) => {
    setSelectedFilters((prev) => {
      const isSelected = prev.region.includes(region);
      const updatedRegions = isSelected
        ? prev.region.filter((item) => item !== region)
        : [...prev.region, region];

      const availableCountries = getCountriesForRegions(updatedRegions);
      setCountryOptions(availableCountries);

      const validCountries = prev.country.filter((country) =>
        availableCountries.some((c: any) => c.name === country)
      );

      const availablePorts = getPortsForCountries(
        validCountries,
        availableCountries
      );
      const validPorts = prev.port.filter((port) =>
        availablePorts.includes(port)
      );

      return {
        ...prev,
        region: updatedRegions,
        country: validCountries,
        port: validPorts,
      };
    });
  }, [getCountriesForRegions, getPortsForCountries]);

  const handleCountrySelect = useCallback((country: string) => {
    setSelectedFilters((prev) => {
      const isSelected = prev.country.includes(country);
      const updatedCountries = isSelected
        ? prev.country.filter((item) => item !== country)
        : [...prev.country, country];

      const availablePorts = getPortsForCountries(updatedCountries, countryOptions);
      setPortOptions(availablePorts);

      const validPorts = prev.port.filter((port) =>
        availablePorts.includes(port)
      );

      return {
        ...prev,
        country: updatedCountries,
        port: validPorts,
      };
    });
  }, [countryOptions, getPortsForCountries]);

  const handlePortSelect = useCallback((port: string) => {
    setSelectedFilters((prev) => {
      const isSelected = prev.port.includes(port);
      return {
        ...prev,
        port: isSelected
          ? prev.port.filter((item) => item !== port)
          : [...prev.port, port],
      };
    });
  }, []);

  const getFilteredOptions = useCallback(
    (
      filterKey: keyof Omit<SearchState, "region" | "country" | "port">,
      searchValue: string
    ) => {
      const options = filterOptions[filterKey] || [];
      if (!searchValue) return options;

      const lowerSearch = searchValue.toLowerCase();
      return options.filter((item: any) => {
        const itemName =
          typeof item === "string" ? item : item?.name || String(item) || "";
        return String(itemName).toLowerCase().includes(lowerSearch);
      });
    },
    [filterOptions]
  );

  const filteredBusinessOptions = useMemo(
    () => ({
      bussiness_channel: getFilteredOptions(
        "bussiness_channel",
        searchText.bussiness_channel
      ),
      cust_sales_area: getFilteredOptions(
        "cust_sales_area",
        searchText.cust_sales_area
      ),
      sales_country: getFilteredOptions(
        "sales_country",
        searchText.sales_country
      ),
      order_number: getFilteredOptions(
        "order_number",
        searchText.order_number
      ),
      cust_code: getFilteredOptions("cust_code", searchText.cust_code),
      cust_name: getFilteredOptions("cust_name", searchText.cust_name),
      vessel_imo_number: getFilteredOptions(
        "vessel_imo_number",
        searchText.vessel_imo_number
      ),
      vessel_name: getFilteredOptions("vessel_name", searchText.vessel_name),
      account_manager: getFilteredOptions(
        "account_manager",
        searchText.account_manager
      ),
    }),
    [getFilteredOptions, searchText]
  );

  const handleOptionSelect = useCallback(
    (
      filterKey: keyof Omit<
        FilterState,
        "region" | "country" | "port" | "tax_document_type" | "status"
      >,
      value: string
    ) => {
      setSelectedFilters((prev) => {
        const currentValues = prev[filterKey] as string[];
        const isSelected = currentValues.includes(value);
        return {
          ...prev,
          [filterKey]: isSelected
            ? currentValues.filter((item) => item !== value)
            : [...currentValues, value],
        };
      });
    },
    []
  );

  const handleCheckboxChange = useCallback(
    (filterKey: "tax_document_type" | "status", value: string) => {
      setSelectedFilters((prev) => {
        const currentValues = prev[filterKey];
        const isSelected = currentValues.includes(value);
        return {
          ...prev,
          [filterKey]: isSelected
            ? currentValues.filter((item) => item !== value)
            : [...currentValues, value],
        };
      });
    },
    []
  );

  const handleSearchChange = useCallback(
    (filterKey: keyof SearchState, value: string) => {
      setSearchText((prev) => ({
        ...prev,
        [filterKey]: value,
      }));
    },
    []
  );

  const handleRemoveFilter = useCallback(
    (filterKey: keyof FilterState, value: string) => {
      if (filterKey === "region") {
        handleRegionSelect(value);
      } else if (filterKey === "country") {
        handleCountrySelect(value);
      } else if (filterKey === "port") {
        handlePortSelect(value);
      } else {
        setSelectedFilters((prev) => ({
          ...prev,
          [filterKey]: (prev[filterKey] as string[]).filter(
            (item) => item !== value
          ),
        }));
      }
    },
    [handleRegionSelect, handleCountrySelect, handlePortSelect]
  );

  const handleApplyFilters = useCallback(() => {
    handleFilterApply(selectedFilters);
  }, [selectedFilters, handleFilterApply]);

  const handleClearAll = useCallback(() => {
    const emptyFilters: FilterState = {
      region: [],
      country: [],
      port: [],
      bussiness_channel: [],
      cust_sales_area: [],
      sales_country: [],
      order_number: [],
      cust_code: [],
      cust_name: [],
      vessel_name: [],
      vessel_imo_number: [],
      account_manager: [],
      tax_document_type: [],
      status: [],
    };
    setSelectedFilters(emptyFilters);
    setSearchText({
      region: "",
      country: "",
      port: "",
      bussiness_channel: "",
      cust_sales_area: "",
      sales_country: "",
      order_number: "",
      cust_code: "",
      cust_name: "",
      vessel_name: "",
      vessel_imo_number: "",
      account_manager: "",
    });
    setCountryOptions([]);
    setPortOptions([]);
    // Auto-apply empty filters
    handleFilterApply(emptyFilters);
  }, [handleFilterApply]);

  // Auto-apply filters only when REMOVING a filter (not when adding)
  useEffect(() => {
    if (prevFiltersRef.current) {
      // Check if any filter was removed (decreased in length)
      const wasFilterRemoved = (
        Object.keys(selectedFilters) as Array<keyof FilterState>
      ).some((key) => {
        const prevLength = prevFiltersRef.current![key].length;
        const currentLength = selectedFilters[key].length;
        return currentLength < prevLength; // Filter was removed
      });

      if (wasFilterRemoved) {
        handleFilterApply(selectedFilters);
      }
    }

    prevFiltersRef.current = JSON.parse(JSON.stringify(selectedFilters));
  }, [selectedFilters, handleFilterApply]);

  

  const renderMultiSelectDropdown = useCallback(
    (
      filterKey: "region" | "country" | "port",
      label: string,
      options: any[],
      isDisabled: boolean = false
    ) => {
      const selectedValues = selectedFilters[filterKey];
      const selectedCount = selectedValues.length;

      const filteredOptions = options.filter((item: any) => {
        const itemName =
          filterKey === "region"
            ? item?.region_name || item?.name || item || ""
            : item?.name || item || "";
        const lowerSearch = String(searchText[filterKey] || "").toLowerCase();
        return String(itemName).toLowerCase().includes(lowerSearch);
      });

      return (
        <div className="tax-filter-item mb-3">
          <label className="tax-filter-label">{label}</label>
          <UncontrolledDropdown
            className="tax-filter-dropdown"
            isOpen={openDropdown === filterKey}
            toggle={() =>
              setOpenDropdown(openDropdown === filterKey ? null : filterKey)
            }
          >
            <DropdownToggle
              className="w-100 sch-drpdwn-toggle rounded-0 border ft-size"
              color="#fff"
              caret
              disabled={isDisabled}
            >
              <span className="text-truncate">
                {selectedCount > 0
                  ? `${label} (${selectedCount})`
                  : `Select ${label}`}
              </span>
              <Down24 color="#007f00" />
            </DropdownToggle>

            <DropdownMenu className="tax-filter-menu w-100 ft-size">
              <div className="tax-filter-search p-2">
                <Input
                  type="text"
                  placeholder={`Type to search...`}
                  value={searchText[filterKey]}
                  onChange={(e) =>
                    handleSearchChange(filterKey, e.currentTarget.value)
                  }
                  onKeyDown={(e) => e.stopPropagation()}
                  className="form-control-sm"
                />
              </div>

              {filteredOptions.length > 0 ? (
                <div
                  className="tax-filter-options"
                  style={{ maxHeight: "250px", overflowY: "auto" }}
                >
                  {filteredOptions.map((option: any, index: number) => {
                    const optionName =
                      filterKey === "region"
                        ? option?.region_name || option?.name || option || ""
                        : option?.name || option || "";
                    const optionNameStr = String(optionName);
                    const isSelected = selectedValues.includes(optionNameStr);

                    return (
                      <DropdownItem
                        key={`${filterKey}_${index}`}
                        className="tax-filter-option p-2"
                        onClick={() => {
                          if (filterKey === "region") {
                            handleRegionSelect(optionNameStr);
                          } else if (filterKey === "country") {
                            handleCountrySelect(optionNameStr);
                          } else if (filterKey === "port") {
                            handlePortSelect(optionNameStr);
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="me-2"
                        />
                        <span>{optionNameStr}</span>
                      </DropdownItem>
                    );
                  })}
                </div>
              ) : (
                <DropdownItem disabled className="text-center p-2">
                  No results found
                </DropdownItem>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>

          {selectedValues.length > 0 && (
            <div className="tax-filter-badges mt-2">
              {selectedValues.map((value: string, index: number) => (
                <span
                  key={`badge_${filterKey}_${index}`}
                  className="badge bg-success me-2 mb-2 p-2"
                >
                  {value}
                  <Remove24
                    onClick={() => handleRemoveFilter(filterKey, value)}
                    className="ms-2"
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      );
    },
    [
      selectedFilters,
      searchText,
      openDropdown,
      handleSearchChange,
      handleRegionSelect,
      handleCountrySelect,
      handlePortSelect,
      handleRemoveFilter,
    ]
  );

  const renderFilterDropdown = useCallback(
    (
      filterKey: keyof Omit<
        FilterState,
        "region" | "country" | "port" | "tax_document_type" | "status"
      >,
      label: string
    ) => {
      const filteredOptions = filteredBusinessOptions[filterKey];
      const selectedCount = selectedFilters[filterKey].length;

      return (
        <div key={filterKey} className="tax-filter-item mb-3">
          <label className="tax-filter-label">{label}</label>
          <UncontrolledDropdown
            className="tax-filter-dropdown"
            isOpen={openDropdown === filterKey}
            toggle={() =>
              setOpenDropdown(openDropdown === filterKey ? null : filterKey)
            }
          >
            <DropdownToggle
              className="w-100 sch-drpdwn-toggle rounded-0 border ft-size"
              color="#fff"
              caret
            >
              <span className="text-truncate">
                {selectedCount > 0
                  ? `${label} (${selectedCount})`
                  : `Select ${label}`}
              </span>
              <Down24 color="#007f00" />
            </DropdownToggle>

            <DropdownMenu className="tax-filter-menu w-100 ft-size">
              <div className="tax-filter-search p-2">
                <Input
                  type="text"
                  placeholder={`Type to search...`}
                  value={searchText[filterKey]}
                  onChange={(e) =>
                    handleSearchChange(filterKey, e.currentTarget.value)
                  }
                  onKeyDown={(e) => e.stopPropagation()}
                  className="form-control-sm"
                />
              </div>

              {filteredOptions.length > 0 ? (
                <div
                  className="tax-filter-options"
                  style={{ maxHeight: "250px", overflowY: "auto" }}
                >
                  {filteredOptions.slice(0, 100).map((option: any, index: number) => {
                    const optionStr =
                      typeof option === "string"
                        ? option
                        : option?.name || String(option) || "";

                    return (
                      <DropdownItem
                        key={`${filterKey}_${index}`}
                        className="tax-filter-option p-2"
                        onClick={() => handleOptionSelect(filterKey, optionStr)}
                      >
                        <input
                          type="checkbox"
                          checked={selectedFilters[filterKey].includes(
                            optionStr
                          )}
                          readOnly
                          className="me-2"
                        />
                        <span>{optionStr}</span>
                      </DropdownItem>
                    );
                  })}
                </div>
              ) : (
                <DropdownItem disabled className="text-center p-2">
                  No results found
                </DropdownItem>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>

          {selectedFilters[filterKey].length > 0 && (
            <div className="tax-filter-badges mt-2">
              {selectedFilters[filterKey].map((value: string, index: number) => (
                <span
                  key={`badge_${filterKey}_${index}`}
                  className="badge bg-success me-2 mb-2 p-2"
                >
                  {value}
                  <Remove24
                    onClick={() => handleRemoveFilter(filterKey, value)}
                    className="ms-2"
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      );
    },
    [
      filteredBusinessOptions,
      selectedFilters,
      searchText,
      openDropdown,
      handleSearchChange,
      handleOptionSelect,
      handleRemoveFilter,
    ]
  );

  const renderCheckboxGroup = useCallback(
    (
      filterKey: "tax_document_type" | "status",
      label: string,
      options: string[]
    ) => {
      return (
        <div className="tax-filter-item mb-3">
          <label className="tax-filter-label">{label}</label>
          <div className="tax-filter-checkbox-group border rounded p-3">
            {options.map((option) => (
              <div key={option} className="form-check mb-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`${filterKey}_${option}`}
                  checked={selectedFilters[filterKey].includes(option)}
                  onChange={() => handleCheckboxChange(filterKey, option)}
                />
                <label
                  className="form-check-label"
                  htmlFor={`${filterKey}_${option}`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div>

          {selectedFilters[filterKey].length > 0 && (
            <div className="tax-filter-badges mt-2">
              {selectedFilters[filterKey].map((value: string, index: number) => (
                <span
                  key={`badge_${filterKey}_${index}`}
                  className="badge bg-success me-2 mb-2 p-2"
                >
                  {value}
                  <Remove24
                    onClick={() => handleRemoveFilter(filterKey, value)}
                    className="ms-2"
                  />
                </span>
              ))}
            </div>
          )}
        </div>
      );
    },
    [selectedFilters, handleCheckboxChange, handleRemoveFilter]
  );

  return (
    <div className="tax-filter-container p-4 border rounded">
              <div className="tax-filter-header mb-4">
        <h5 className="mb-0">Filter</h5>
        <div className="tax-filter-actions">
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleClearAll}
            disabled={!hasActiveFilters}
          >
            Clear All
          </button>
          <button
            className="btn btn-sm btn-success"
            onClick={handleApplyFilters}
            disabled={!hasActiveFilters}
          >
            Apply Filters
          </button>
        </div>
      </div>
      <div className="tax-filter-content">
        <div className="tax-filter-section mb-4">
          {renderCheckboxGroup(
            "tax_document_type",
            "Tax Document Type",
            taxDocumentTypes
          )}
          {renderCheckboxGroup("status", "Status", statusOptions)}
        </div>
        <div className="tax-filter-region-section mb-4 pb-3 border-bottom">
          {renderMultiSelectDropdown("region", "Supply Region", regionData, false)}
          {renderMultiSelectDropdown(
            "country",
            "Port Country",
            countryOptions,
            selectedFilters.region.length === 0
          )}
          {renderMultiSelectDropdown(
            "port",
            "Port Name",
            portOptions,
            selectedFilters.country.length === 0
          )}
        </div>
        <div className="tax-filter-section mb-4 pb-3 border-bottom">
          {renderFilterDropdown("order_number", "Order Number")}
          {renderFilterDropdown("bussiness_channel", "Business Channel")}
          {renderFilterDropdown("cust_sales_area", "Customer Sales Area")}
          {/* {renderFilterDropdown("sales_country", "Sales Country")} */}
          {renderFilterDropdown("cust_code", "Customer Sold-To-ID")}
          {renderFilterDropdown("cust_name", "Customer Name")}
          {renderFilterDropdown("vessel_name", "Vessel Name")}
          {renderFilterDropdown("vessel_imo_number", "Vessel IMO Number")}
          {/* {renderFilterDropdown("account_manager", "Account Manager")} */}
        </div>
      </div>
     
    </div>
  );
};

export default TaxFilter;